import { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
} from "@mui/material";
import { format } from "date-fns";
import { Download } from "@mui/icons-material";
import Loading from "../ui/Loading";
import { exportToExcel } from "../../utils/exportToExcel";

const CompletionByRepTable = ({ data, isLoading, error, filters }) => {
  const [orderBy, setOrderBy] = useState("rank");
  const [order, setOrder] = useState("asc");

  if (isLoading) return <Loading />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!data || data.length === 0)
    return <Typography>No rep data available</Typography>;

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = [...data].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    // Handle null values (N/A) - push to end
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;

    // String comparison for repName
    if (orderBy === "repName") {
      aVal = (aVal || "").toLowerCase();
      bVal = (bVal || "").toLowerCase();
      if (order === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    }

    // Date comparison for lastSync
    if (orderBy === "lastSync") {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    // Numeric comparison
    if (order === "asc") {
      return aVal - bVal;
    } else {
      return bVal - aVal;
    }
  });

  const handleExport = () => {
    const exportData = sortedData.map((rep) => ({
      "Rep Name": rep.repName || "Unknown",
      Rank: rep.rank !== null ? rep.rank : "N/A",
      "Completion %":
        rep.completionPercent !== null
          ? `${rep.completionPercent.toFixed(1)}%`
          : "N/A",
      Completed: rep.completedCalls || 0,
      Past: rep.pastCalls || 0,
      Today: rep.todayCalls || 0,
      Incomplete: rep.incompleteCalls || 0,
      Future: rep.futureCalls || 0,
      "Total Scheduled": rep.totalScheduled || 0,
      "Last Sync": rep.lastSync
        ? format(new Date(rep.lastSync), "MMM dd, yyyy HH:mm")
        : "Never",
    }));

    const filename = `rep-completion-${filters.year}-${String(filters.month).padStart(2, "0")}-${format(new Date(), "yyyyMMdd-HHmmss")}`;
    exportToExcel(exportData, filename);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Completion by Rep ({sortedData.length} reps)
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Download />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Box>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table
          stickyHeader
          size="small"
        >
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "repName"}
                  direction={orderBy === "repName" ? order : "asc"}
                  onClick={() => handleSort("repName")}
                >
                  Rep Name
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === "rank"}
                  direction={orderBy === "rank" ? order : "asc"}
                  onClick={() => handleSort("rank")}
                >
                  Rank
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "completionPercent"}
                  direction={orderBy === "completionPercent" ? order : "asc"}
                  onClick={() => handleSort("completionPercent")}
                >
                  Completion %
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "completedCalls"}
                  direction={orderBy === "completedCalls" ? order : "asc"}
                  onClick={() => handleSort("completedCalls")}
                >
                  Completed
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "pastCalls"}
                  direction={orderBy === "pastCalls" ? order : "asc"}
                  onClick={() => handleSort("pastCalls")}
                >
                  Past
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "todayCalls"}
                  direction={orderBy === "todayCalls" ? order : "asc"}
                  onClick={() => handleSort("todayCalls")}
                >
                  Today
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "incompleteCalls"}
                  direction={orderBy === "incompleteCalls" ? order : "asc"}
                  onClick={() => handleSort("incompleteCalls")}
                >
                  Incomplete
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "futureCalls"}
                  direction={orderBy === "futureCalls" ? order : "asc"}
                  onClick={() => handleSort("futureCalls")}
                >
                  Future
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "totalScheduled"}
                  direction={orderBy === "totalScheduled" ? order : "asc"}
                  onClick={() => handleSort("totalScheduled")}
                >
                  Total Scheduled
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === "lastSync"}
                  direction={orderBy === "lastSync" ? order : "asc"}
                  onClick={() => handleSort("lastSync")}
                >
                  Last Sync
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((rep, index) => (
              <TableRow
                key={rep.userId || index}
                hover
              >
                <TableCell>{rep.repName || "Unknown"}</TableCell>
                <TableCell align="center">
                  {rep.rank !== null ? rep.rank : "-"}
                </TableCell>
                <TableCell align="right">
                  {rep.completionPercent !== null
                    ? `${rep.completionPercent.toFixed(1)}%`
                    : "N/A"}
                </TableCell>
                <TableCell align="right">{rep.completedCalls || 0}</TableCell>
                <TableCell align="right">{rep.pastCalls || 0}</TableCell>
                <TableCell align="right">{rep.todayCalls || 0}</TableCell>
                <TableCell align="right">{rep.incompleteCalls || 0}</TableCell>
                <TableCell align="right">{rep.futureCalls || 0}</TableCell>
                <TableCell align="right">{rep.totalScheduled || 0}</TableCell>
                <TableCell align="center">
                  {rep.lastSync
                    ? format(new Date(rep.lastSync), "MMM dd, yyyy HH:mm")
                    : "Never"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default CompletionByRepTable;
